import Text from "@/components/Text";
import { Button } from "@/components/ui/button";
import { ShieldCheck } from "lucide-react";
import { type Dispatch, type FC, type SetStateAction } from "react";
import SearchRestaurant from "../SearchRestaurant";
import type { RestaurantAutocompleteResult } from "@/API";
import { useTranslation } from "react-i18next";

type Props = {
  restaurants: RestaurantAutocompleteResult[];
  setRestaurants: Dispatch<SetStateAction<RestaurantAutocompleteResult[]>>;
  nextStep: VoidFunction;
};

const OnboardingAddRestaurantsStep1: FC<Props> = ({
  restaurants,
  setRestaurants,
  nextStep,
}) => {
  const { t } = useTranslation();

  return (
    <div className="flex flex-col gap-[24px]">
      {/* Step */}
      <div className="bg-[#F4F4F4] py-[4px] px-[8px] rounded-[40px] w-[fit-content]">
        <Text fontSize={14} fontWeight={500} color="#393939">
          {t("step_progress", { step: 1, end: 3 })}
        </Text>
      </div>

      {/* Header */}
      <div className="flex flex-col gap-[10px]">
        <Text fontSize={28} fontWeight={700}>
          {t("onboarding_add_restaurants_step_1_title")}
        </Text>
        <Text fontSize={14} fontWeight={400} color="#393939">
          {t("onboarding_add_restaurants_step_1_description")}
        </Text>
      </div>

      {/* Content */}
      <div className="flex flex-col gap-[12px]">
        <Text fontSize={14} fontWeight={400} color="#161616">
          {t("onboarding_add_restaurants_step_1_1")}
        </Text>
        <SearchRestaurant
          onChange={(value) => {
            setRestaurants((prevState) => [...prevState, value]);
          }}
        />
        <Text fontSize={14} fontWeight={400} color="#6F6F6F">
          {t("onboarding_add_restaurants_step_1_2")}
        </Text>
      </div>

      {/* Restaurants */}
      {restaurants.length > 0 && (
        <div className="flex flex-col gap-[8px] max-h-[250px] overflow-y-auto">
          <Text fontSize={14} fontWeight={400} color="#161616">
            {t("onboarding_add_restaurants_step_1_5", {
              count: restaurants.length,
            })}
          </Text>
          {restaurants.map((restaurant) => (
            <div
              key={restaurant.id}
              className="p-[16px] rounded-[16px] flex items-center justify-between"
              style={{ border: "1px solid #C7D1DB" }}
            >
              <div className="flex gap-[12px] items-center">
                <img
                  className="h-[40px] w-[40px] min-h-[40px] min-w-[40px] rounded-[50%]"
                  src={restaurant?.imageUrl ?? undefined}
                />
                <img
                  className="h-[40px] w-[40px] min-h-[40px] min-w-[40px] rounded-[50%] ml-[-32px]"
                  src={`/partner/${restaurant.platform.toLowerCase()}.png`}
                />
                <div className="flex flex-col">
                  <Text fontSize={14} fontWeight={500} color="#393939">
                    {restaurant.name}
                  </Text>
                  <Text fontSize={12} fontWeight={500} color="#535862">
                    {restaurant.address}
                  </Text>
                </div>
              </div>

              <Button
                onClick={() => {
                  setRestaurants((prevState) =>
                    prevState.filter((it) => it.id !== restaurant.id),
                  );
                }}
              >
                {t("remove")}
              </Button>
            </div>
          ))}
        </div>
      )}

      {/* Action */}
      <Button disabled={restaurants.length === 0} onClick={nextStep}>
        {t("onboarding_add_restaurants_step_1_3")}
      </Button>

      {/* Footer */}
      <div
        className="bg-[#F4F4F4] rounded-[8px] p-[16px] flex items-center gap-[12px] w-[100%]"
        style={{ border: "1px solid #E0E0E0" }}
      >
        <ShieldCheck color="#A8A8A8" height={24} width={24} />
        <Text fontSize={14} fontWeight={400} color="#525252">
          {t("onboarding_add_restaurants_step_1_4")}
        </Text>
      </div>
    </div>
  );
};

export default OnboardingAddRestaurantsStep1;
